import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const schema = z.object({
  name: z.string().trim().min(1).max(100),
  phone: z.string().regex(/^\d{10}$/),
  pincode: z.string().regex(/^\d{6}$/),
});

const RECIPIENTS = ["nearbyservices.dm@gmail.com", "tataplay.newdth@gmail.com"];
const SITE_NAME = "Nearby Services";
const SENDER_DOMAIN = "notify.nearbyservices.co.in";
const FROM_DOMAIN = "nearbyservices.co.in";

export const sendCallback = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => schema.parse(input))
  .handler(async ({ data }) => {
    const supabaseUrl = process.env.SUPABASE_URL;
    const supabaseServiceKey = process.env.SUPABASE_SERVICE_ROLE_KEY;
    if (!supabaseUrl || !supabaseServiceKey) {
      throw new Error("Email service is not configured");
    }

    const [{ createClient }, React, { render }, { template }] = await Promise.all([
      import("@supabase/supabase-js"),
      import("react"),
      import("react-email"),
      import("@/lib/email-templates/lead-notification"),
    ]);

    const supabase = createClient(supabaseUrl, supabaseServiceKey);

    const receivedAt = new Date().toISOString();
    const templateData = {
      name: data.name,
      phone: data.phone,
      pincode: data.pincode,
      receivedAt,
    };

    const element = React.createElement(template.component, templateData);
    const html = await render(element);
    const text = await render(element, { plainText: true });
    const subject =
      typeof template.subject === "function"
        ? template.subject(templateData)
        : template.subject;

    const results = await Promise.all(
      RECIPIENTS.map(async (to) => {
        const normalized = to.toLowerCase();
        const messageId = crypto.randomUUID();

        // Skip if recipient is on suppression list
        const { data: suppressed } = await supabase
          .from("suppressed_emails")
          .select("id")
          .eq("email", normalized)
          .maybeSingle();
        if (suppressed) {
          return { to, status: "suppressed" as const };
        }

        // Ensure an unsubscribe token exists
        let unsubscribeToken: string;
        const { data: existing } = await supabase
          .from("email_unsubscribe_tokens")
          .select("token")
          .eq("email", normalized)
          .maybeSingle();
        if (existing?.token) {
          unsubscribeToken = existing.token;
        } else {
          const bytes = new Uint8Array(32);
          crypto.getRandomValues(bytes);
          unsubscribeToken = Array.from(bytes)
            .map((b) => b.toString(16).padStart(2, "0"))
            .join("");
          await supabase
            .from("email_unsubscribe_tokens")
            .upsert(
              { token: unsubscribeToken, email: normalized },
              { onConflict: "email", ignoreDuplicates: true },
            );
          const { data: stored } = await supabase
            .from("email_unsubscribe_tokens")
            .select("token")
            .eq("email", normalized)
            .maybeSingle();
          if (stored?.token) unsubscribeToken = stored.token;
        }

        await supabase.from("email_send_log").insert({
          message_id: messageId,
          template_name: "lead-notification",
          recipient_email: to,
          status: "pending",
        });

        const { error: enqueueError } = await supabase.rpc("enqueue_email", {
          queue_name: "transactional_emails",
          payload: {
            message_id: messageId,
            to,
            from: `${SITE_NAME} <leads@${FROM_DOMAIN}>`,
            sender_domain: SENDER_DOMAIN,
            subject,
            html,
            text,
            purpose: "transactional",
            label: "lead-notification",
            idempotency_key: messageId,
            unsubscribe_token: unsubscribeToken,
            queued_at: new Date().toISOString(),
          },
        });

        if (enqueueError) {
          console.error("Failed to enqueue lead email", enqueueError);
          await supabase.from("email_send_log").insert({
            message_id: messageId,
            template_name: "lead-notification",
            recipient_email: to,
            status: "failed",
            error_message: enqueueError.message,
          });
          return { to, status: "failed" as const };
        }

        return { to, status: "queued" as const };
      }),
    );

    return { ok: true, results };
  });
